/**
 * src/components/map/CategoryPills.tsx
 *
 * Floating horizontal category pill selector for MapLibre Travel Photo Map.
 * Provides touch-friendly scrolling, active states, photo counts, and sub-12ms re-indexing.
 */

import React, { useRef, useEffect } from 'react';
import { Layers } from 'lucide-react';

export interface CategoryPillsProps {
  categories: string[];
  counts: Record<string, number>;
  selectedCategory: string;
  onSelectCategory: (category: string) => void;
  className?: string;
}

// Preset color palette for category bullets
const CATEGORY_COLOR_PALETTE: Record<string, string> = {
  Tutte: '#38bdf8', // Sky 400
  'Con me': '#f43f5e', // Rose 500
  Persone: '#f43f5e',
  Panorami: '#10b981', // Emerald 500
  Paesaggi: '#10b981',
  Natura: '#10b981',
  Dettagli: '#f59e0b', // Amber 500
  Architettura: '#f59e0b',
  Cibo: '#8b5cf6', // Violet 500
  Street: '#06b6d4', // Cyan 500
  Altre: '#94a3b8', // Slate 400
};

export function getCategoryBulletColor(category: string): string {
  if (CATEGORY_COLOR_PALETTE[category]) {
    return CATEGORY_COLOR_PALETTE[category];
  }
  // Deterministic color generation for custom user folder names
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 70%, 55%)`;
}

export const CategoryPills: React.FC<CategoryPillsProps> = ({
  categories,
  counts,
  selectedCategory,
  onSelectCategory,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const activePillRef = useRef<HTMLButtonElement>(null);

  // Auto-scroll the active pill into view on change
  useEffect(() => {
    if (activePillRef.current && containerRef.current) {
      activePillRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [selectedCategory]);

  const pillList = ['Tutte', ...categories.filter((c) => c !== 'Tutte')];

  return (
    <div
      className={`pointer-events-none w-full max-w-full select-none ${className}`}
      role="region"
      aria-label="Filtri per categoria"
    >
      <div
        ref={containerRef}
        className="pointer-events-auto flex items-center gap-1.5 overflow-x-auto no-scrollbar px-3 py-2 scroll-smooth"
        style={{
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
        role="tablist"
        aria-orientation="horizontal"
      >
        {pillList.map((category) => {
          const isSelected = selectedCategory === category;
          const count = counts[category] ?? (category === 'Tutte' ? counts['Tutte'] ?? 0 : 0);
          const bulletColor = getCategoryBulletColor(category);

          return (
            <button
              key={category}
              ref={isSelected ? activePillRef : null}
              type="button"
              role="tab"
              aria-selected={isSelected}
              onClick={() => onSelectCategory(category)}
              className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                isSelected
                  ? 'bg-sky-500 text-white shadow-lg shadow-sky-500/30 border border-sky-400 scale-[1.02]'
                  : 'bg-slate-900/80 backdrop-blur-md text-slate-300 hover:text-white hover:bg-slate-800/90 border border-slate-700/60 shadow-md'
              }`}
            >
              {category === 'Tutte' ? (
                <Layers className={`w-3.5 h-3.5 ${isSelected ? 'text-white' : 'text-sky-400'}`} />
              ) : (
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0 transition-transform group-hover:scale-125"
                  style={{ backgroundColor: bulletColor }}
                  aria-hidden="true"
                />
              )}

              <span>{category}</span>

              <span
                className={`ml-0.5 px-1.5 py-0.2 rounded-full text-[10px] font-mono leading-none ${
                  isSelected
                    ? 'bg-white/25 text-white font-semibold'
                    : 'bg-slate-800 text-slate-400 border border-slate-700/50'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryPills;
